import { useState } from 'react';
import type { InventoryItem } from '../types/inventory.types';
import { InventoryService } from '../services';

/**
 * Toplu işlemler için custom hook
 * @param companyId - Şirket ID'si
 * @param inventoryItems - Envanter ürünleri
 * @returns Toplu işlem fonksiyonları ve state'leri
 */
export const useBulkOperations = (
  companyId: string | null,
  inventoryItems: InventoryItem[]
) => {
  // Toplu silme
  const [bulkDeleting, setBulkDeleting] = useState<boolean>(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState<boolean>(false);

  // Toplu pasife alma
  const [bulkInactivating, setBulkInactivating] = useState<boolean>(false);
  const [showBulkInactivateDialog, setShowBulkInactivateDialog] = useState<boolean>(false);

  // Toplu fiyat güncelleme
  const [bulkPriceUpdating, setBulkPriceUpdating] = useState<boolean>(false);
  const [showBulkPriceUpdateDialog, setShowBulkPriceUpdateDialog] = useState<boolean>(false);
  const [priceUpdateType, setPriceUpdateType] = useState<'percentage' | 'fixed'>('percentage');
  const [priceUpdateValue, setPriceUpdateValue] = useState<number>(0);
  const [priceUpdateTarget, setPriceUpdateTarget] = useState<'sale' | 'purchase'>('sale');

  // Toplu resim silme
  const [bulkDeletingImages, setBulkDeletingImages] = useState<boolean>(false);
  const [showBulkDeleteImagesDialog, setShowBulkDeleteImagesDialog] = useState<boolean>(false);

  // Toplu e-ticaret
  const [bulkEcommerceProcessing, setBulkEcommerceProcessing] = useState<boolean>(false);
  const [showBulkEcommerceDialog, setShowBulkEcommerceDialog] = useState<boolean>(false);
  const [bulkEcommerceAction, setBulkEcommerceAction] = useState<'open' | 'close' | null>(null);

  // Toplu KDV oranı güncelleme
  const [bulkVatRateUpdating, setBulkVatRateUpdating] = useState<boolean>(false);
  const [showBulkVatRateDialog, setShowBulkVatRateDialog] = useState<boolean>(false);
  const [bulkVatRate, setBulkVatRate] = useState<number>(18);

  // Toplu ÖKC (Departman No) güncelleme
  const [bulkDepartmentNoUpdating, setBulkDepartmentNoUpdating] = useState<boolean>(false);
  const [showBulkDepartmentNoDialog, setShowBulkDepartmentNoDialog] = useState<boolean>(false);
  const [bulkDepartmentNo, setBulkDepartmentNo] = useState<number>(1);

  // Toplu KDV + ÖKC güncelleme
  const [bulkVatAndDeptUpdating, setBulkVatAndDeptUpdating] = useState<boolean>(false);
  const [showBulkVatAndDeptDialog, setShowBulkVatAndDeptDialog] = useState<boolean>(false);

  // Toplu QR Menu işlemleri
  const [bulkQRMenuProcessing, setBulkQRMenuProcessing] = useState<boolean>(false);
  const [showBulkQRMenuModal, setShowBulkQRMenuModal] = useState<boolean>(false);
  const [bulkQRMenuProgress, setBulkQRMenuProgress] = useState<number>(0);
  const [bulkQRMenuCurrentItem, setBulkQRMenuCurrentItem] = useState<string>('');
  const [bulkQRMenuResults, setBulkQRMenuResults] = useState<{
    success: number;
    failed: number;
    total: number;
    alreadyOpen?: number;
    alreadyOpenNames?: string[];
  }>({ success: 0, failed: 0, total: 0 });

  // Toplu Kiosk işlemleri
  const [bulkKioskProcessing, setBulkKioskProcessing] = useState<boolean>(false);
  const [showBulkKioskModal, setShowBulkKioskModal] = useState<boolean>(false);
  const [bulkKioskProgress, setBulkKioskProgress] = useState<number>(0);
  const [bulkKioskCurrentItem, setBulkKioskCurrentItem] = useState<string>('');
  const [bulkKioskResults, setBulkKioskResults] = useState<{
    success: number;
    failed: number;
    total: number;
    alreadyOpen?: number;
    alreadyOpenNames?: string[];
  }>({ success: 0, failed: 0, total: 0 });

  // Toplu depo atama
  const [bulkWarehouseAssignProcessing, setBulkWarehouseAssignProcessing] = useState<boolean>(false);
  const [showBulkWarehouseAssignModal, setShowBulkWarehouseAssignModal] = useState<boolean>(false);

  /**
   * Toplu silme işlemini gerçekleştir (SQL API)
   */
  const executeBulkDelete = async (selectedItems: string[]): Promise<void> => {
    if (!companyId || selectedItems.length === 0) {
      return;
    }

    setBulkDeleting(true);
    try {
      console.log('🗑️ [BULK DELETE] SQL ile toplu silme başlatıldı:', selectedItems.length, 'ürün');
      
      // ✅ SQL API ile toplu silme (soft delete)
      const count = await InventoryService.bulkDeleteItems(companyId, selectedItems); // ✅ companyId eklendi
      
      console.log(`✅ [BULK DELETE] ${count} ürün SQL'den başarıyla silindi`);
      
    } finally {
      setBulkDeleting(false);
    }
  };

  /**
   * Toplu pasife alma işlemini gerçekleştir (SQL API)
   */
  const executeBulkInactivate = async (selectedItems: string[]): Promise<void> => {
    if (!companyId || selectedItems.length === 0) {
      return;
    }

    setBulkInactivating(true);
    try {
      console.log('🔄 [BULK INACTIVATE] SQL ile toplu pasife alma başlatıldı:', selectedItems.length, 'ürün');
      
      // ✅ SQL API ile toplu durum güncelleme (isActive = false)
      const count = await InventoryService.bulkUpdateStatus(companyId, selectedItems, false); // ✅ companyId eklendi
      
      console.log(`✅ [BULK INACTIVATE] ${count} ürün SQL'de pasife alındı`);
      
    } finally {
      setBulkInactivating(false);
    }
  };

  /**
   * Toplu fiyat güncelleme işlemini gerçekleştir (SQL API)
   */
  const executeBulkPriceUpdate = async (selectedItems: string[]): Promise<void> => {
    if (!companyId || selectedItems.length === 0) {
      return;
    }

    setBulkPriceUpdating(true);
    try {
      console.log('💰 [BULK PRICE UPDATE] SQL ile toplu fiyat güncelleme başlatıldı:', selectedItems.length, 'ürün');
      
      // ✅ SQL API ile toplu fiyat güncelleme
      const count = await InventoryService.bulkUpdatePrice(
        companyId, // ✅ companyId eklendi
        selectedItems,
        priceUpdateType,
        priceUpdateValue,
        priceUpdateTarget
      );
      
      console.log(`✅ [BULK PRICE UPDATE] ${count} ürün fiyatı SQL'de güncellendi`);
      
    } finally {
      setBulkPriceUpdating(false);
    }
  };

  /**
   * Toplu resim silme işlemini gerçekleştir (SQL API)
   */
  const executeBulkDeleteImages = async (selectedItems: string[]): Promise<void> => {
    if (!companyId || selectedItems.length === 0) {
      return;
    }

    setBulkDeletingImages(true);
    try {
      // SQL API ile her ürünün imageUrl'ini null olarak güncelle
      for (const itemId of selectedItems) {
        await InventoryService.updateItem(companyId, itemId, {
          id: itemId,
          companyId: companyId,
          imageUrl: null,
          imageUrls: []
        });
      }
      console.log(`✅ ${selectedItems.length} ürünün resimleri silindi`);
    } finally {
      setBulkDeletingImages(false);
    }
  };

  /**
   * Toplu KDV oranı güncelleme işlemini gerçekleştir (SQL API)
   */
  const executeBulkVatRateUpdate = async (selectedItems: string[]): Promise<void> => {
    if (!companyId || selectedItems.length === 0) {
      return;
    }

    setBulkVatRateUpdating(true);
    try {
      console.log(`📊 [BULK VAT RATE UPDATE] SQL ile toplu KDV güncelleme başlatıldı: ${selectedItems.length} ürün, KDV=%${bulkVatRate}`);
      
      // ✅ SQL API ile toplu KDV oranı güncelleme
      const count = await InventoryService.bulkUpdateVatRate(companyId, selectedItems, bulkVatRate); // ✅ companyId eklendi
      
      console.log(`✅ [BULK VAT RATE UPDATE] ${count} ürün KDV oranı SQL'de güncellendi`);
      
    } finally {
      setBulkVatRateUpdating(false);
    }
  };

  /**
   * Toplu ÖKC (Departman No) güncelleme işlemini gerçekleştir (SQL API)
   */
  const executeBulkDepartmentNoUpdate = async (selectedItems: string[]): Promise<void> => {
    if (!companyId || selectedItems.length === 0) {
      return;
    }

    setBulkDepartmentNoUpdating(true);
    try {
      console.log(`🔢 [BULK DEPARTMENT NO UPDATE] SQL ile toplu ÖKC güncelleme başlatıldı: ${selectedItems.length} ürün, ÖKC=${bulkDepartmentNo}`);
      
      // ✅ SQL API ile toplu ÖKC güncelleme
      const count = await InventoryService.bulkUpdateDepartmentNo(companyId, selectedItems, bulkDepartmentNo); // ✅ companyId eklendi
      
      console.log(`✅ [BULK DEPARTMENT NO UPDATE] ${count} ürün ÖKC SQL'de güncellendi`);
      
    } finally {
      setBulkDepartmentNoUpdating(false);
    }
  };

  /**
   * Toplu KDV + ÖKC güncelleme işlemini gerçekleştir (SQL API)
   */
  const executeBulkVatAndDeptUpdate = async (selectedItems: string[]): Promise<void> => {
    if (!companyId || selectedItems.length === 0) {
      return;
    }

    setBulkVatAndDeptUpdating(true);
    try {
      console.log(`🔧 [BULK VAT+DEPT UPDATE] SQL ile toplu KDV+ÖKC güncelleme başlatıldı: ${selectedItems.length} ürün`);
      
      // ✅ SQL API ile toplu KDV + ÖKC güncelleme
      const count = await InventoryService.bulkUpdateVatAndDepartment(selectedItems, bulkVatRate, bulkDepartmentNo);
      
      console.log(`✅ [BULK VAT+DEPT UPDATE] ${count} ürün KDV ve ÖKC SQL'de güncellendi`);
      
    } finally {
      setBulkVatAndDeptUpdating(false);
    }
  };

  /**
   * Toplu QR Menu açma/kapama işlemini gerçekleştir
   */
  const executeBulkToggleQRMenu = async (
    selectedItems: string[],
    inventoryItems: InventoryItem[],
    onUpdateItem: (itemId: string, updates: Partial<InventoryItem>) => void
  ): Promise<{ success: number; failed: number; total: number }> => {
    if (!companyId || selectedItems.length === 0) {
      return { success: 0, failed: 0, total: 0 };
    }

    setBulkQRMenuProcessing(true);
    setBulkQRMenuProgress(0);

    try {
      // Seçili ürünleri al ve SADECE KAPALI olanları işle
      const selectedProducts = inventoryItems.filter(item => selectedItems.includes(item.id));
      const itemsToOpen = selectedProducts.filter(item => item.isQRMenu !== true);
      
      console.log('🔄 [BULK QR MENU TOGGLE] İşlenecek kapalı ürünler:', itemsToOpen.length);
      
      setBulkQRMenuResults({
        success: 0,
        failed: 0,
        total: itemsToOpen.length
      });
      
      let successCount = 0;
      let failedCount = 0;
      
      // Sadece kapalı ürünleri aç
      for (let i = 0; i < itemsToOpen.length; i++) {
        const item = itemsToOpen[i];
        setBulkQRMenuCurrentItem(item.name);
        
        try {
          const result = await InventoryService.toggleQRMenuStatus(companyId, item.id, true);
          
          if (result.success) {
            successCount++;
            
            // State güncelleme callback'i
            onUpdateItem(item.id, {
              isQRMenu: true,
              qrMenuSentAt: new Date().toISOString(),
              qrMenuRemovedAt: undefined
            });
          } else {
            failedCount++;
          }
        } catch (error) {
          console.error(`❌ [BULK QR MENU TOGGLE] ${item.name} için hata:`, error);
          failedCount++;
        }
        
        // İlerleme güncelle
        const progress = Math.round(((i + 1) / itemsToOpen.length) * 100);
        setBulkQRMenuProgress(progress);
        setBulkQRMenuResults({
          success: successCount,
          failed: failedCount,
          total: itemsToOpen.length
        });
      }
      
      return { success: successCount, failed: failedCount, total: itemsToOpen.length };
      
    } finally {
      setBulkQRMenuProcessing(false);
    }
  };

  /**
   * Toplu Kiosk açma/kapama işlemini gerçekleştir
   */
  const executeBulkToggleKiosk = async (
    selectedItems: string[],
    inventoryItems: InventoryItem[],
    onUpdateItem: (itemId: string, updates: Partial<InventoryItem>) => void
  ): Promise<{ success: number; failed: number; total: number }> => {
    if (!companyId || selectedItems.length === 0) {
      return { success: 0, failed: 0, total: 0 };
    }

    setBulkKioskProcessing(true);
    setBulkKioskProgress(0);

    try {
      // Seçili ürünleri al ve SADECE KAPALI olanları işle
      const selectedProducts = inventoryItems.filter(item => selectedItems.includes(item.id));
      const itemsToOpen = selectedProducts.filter(item => item.isKiosk !== true);
      
      console.log('🔄 [BULK KIOSK TOGGLE] İşlenecek kapalı ürünler:', itemsToOpen.length);
      
      setBulkKioskResults({
        success: 0,
        failed: 0,
        total: itemsToOpen.length
      });
      
      let successCount = 0;
      let failedCount = 0;
      
      // Sadece kapalı ürünleri aç
      for (let i = 0; i < itemsToOpen.length; i++) {
        const item = itemsToOpen[i];
        setBulkKioskCurrentItem(item.name);
        
        try {
          const result = await InventoryService.toggleKioskStatus(companyId, item.id, true);
          
          if (result.success) {
            successCount++;
            
            // State güncelleme callback'i
            onUpdateItem(item.id, {
              isKiosk: true,
              kioskSentAt: new Date().toISOString(),
              kioskRemovedAt: undefined
            });
          } else {
            failedCount++;
          }
        } catch (error) {
          console.error(`❌ [BULK KIOSK TOGGLE] ${item.name} için hata:`, error);
          failedCount++;
        }
        
        // İlerleme güncelle
        const progress = Math.round(((i + 1) / itemsToOpen.length) * 100);
        setBulkKioskProgress(progress);
        setBulkKioskResults({
          success: successCount,
          failed: failedCount,
          total: itemsToOpen.length
        });
      }
      
      return { success: successCount, failed: failedCount, total: itemsToOpen.length };
      
    } finally {
      setBulkKioskProcessing(false);
    }
  };

  /**
   * Toplu depo atama işlemini gerçekleştir
   */
  const executeBulkAssignWarehouse = async (
    selectedItems: string[],
    warehouseId: string
  ): Promise<{ count: number }> => {
    if (!companyId || selectedItems.length === 0) {
      return { count: 0 };
    }

    setBulkWarehouseAssignProcessing(true);

    try {
      console.log(`📦 [BULK ASSIGN WAREHOUSE] ${selectedItems.length} ürün depoya atanıyor...`);
      
      const result = await InventoryService.bulkAssignWarehouse(
        companyId,
        selectedItems,
        warehouseId,
        0 // Başlangıç miktarı 0
      );
      
      console.log(`✅ [BULK ASSIGN WAREHOUSE] ${result.count} ürün depoya atandı`);
      
      return result;
      
    } finally {
      setBulkWarehouseAssignProcessing(false);
    }
  };

  /**
   * Toplu e-ticaret işlemini gerçekleştir (SQL API)
   */
  const executeBulkEcommerce = async (
    selectedItems: string[],
    action: 'open' | 'close'
  ): Promise<void> => {
    if (!companyId || selectedItems.length === 0) {
      return;
    }

    setBulkEcommerceProcessing(true);
    try {
      console.log(`🛒 [BULK ECOMMERCE] SQL ile toplu e-ticaret başlatıldı: ${selectedItems.length} ürün, ${action}`);
      
      // ✅ SQL API ile toplu e-ticaret güncelleme
      const enable = action === 'open';
      const count = await InventoryService.bulkToggleEcommerce(companyId, selectedItems, enable); // ✅ companyId eklendi
      
      console.log(`✅ [BULK ECOMMERCE] ${count} ürün SQL'de güncellendi`);
      
    } finally {
      setBulkEcommerceProcessing(false);
    }
  };

  return {
    // Toplu silme
    bulkDeleting,
    showBulkDeleteDialog,
    setShowBulkDeleteDialog,
    executeBulkDelete,

    // Toplu pasife alma
    bulkInactivating,
    showBulkInactivateDialog,
    setShowBulkInactivateDialog,
    executeBulkInactivate,

    // Toplu fiyat güncelleme
    bulkPriceUpdating,
    showBulkPriceUpdateDialog,
    setShowBulkPriceUpdateDialog,
    priceUpdateType,
    setPriceUpdateType,
    priceUpdateValue,
    setPriceUpdateValue,
    priceUpdateTarget,
    setPriceUpdateTarget,
    executeBulkPriceUpdate,

    // Toplu resim silme
    bulkDeletingImages,
    showBulkDeleteImagesDialog,
    setShowBulkDeleteImagesDialog,
    executeBulkDeleteImages,

    // Toplu e-ticaret
    bulkEcommerceProcessing,
    showBulkEcommerceDialog,
    setShowBulkEcommerceDialog,
    bulkEcommerceAction,
    setBulkEcommerceAction,
    executeBulkEcommerce,

    // Toplu KDV oranı güncelleme
    bulkVatRateUpdating,
    showBulkVatRateDialog,
    setShowBulkVatRateDialog,
    bulkVatRate,
    setBulkVatRate,
    executeBulkVatRateUpdate,

    // Toplu ÖKC (Departman No) güncelleme
    bulkDepartmentNoUpdating,
    showBulkDepartmentNoDialog,
    setShowBulkDepartmentNoDialog,
    bulkDepartmentNo,
    setBulkDepartmentNo,
    executeBulkDepartmentNoUpdate,

    // Toplu KDV + ÖKC güncelleme
    bulkVatAndDeptUpdating,
    showBulkVatAndDeptDialog,
    setShowBulkVatAndDeptDialog,
    executeBulkVatAndDeptUpdate,

    // Toplu QR Menu işlemleri
    bulkQRMenuProcessing,
    showBulkQRMenuModal,
    setShowBulkQRMenuModal,
    bulkQRMenuProgress,
    setBulkQRMenuProgress,
    bulkQRMenuCurrentItem,
    setBulkQRMenuCurrentItem,
    bulkQRMenuResults,
    setBulkQRMenuResults,
    executeBulkToggleQRMenu,

    // Toplu Kiosk işlemleri
    bulkKioskProcessing,
    showBulkKioskModal,
    setShowBulkKioskModal,
    bulkKioskProgress,
    setBulkKioskProgress,
    bulkKioskCurrentItem,
    setBulkKioskCurrentItem,
    bulkKioskResults,
    setBulkKioskResults,
    executeBulkToggleKiosk,

    // Toplu depo atama
    bulkWarehouseAssignProcessing,
    showBulkWarehouseAssignModal,
    setShowBulkWarehouseAssignModal,
    executeBulkAssignWarehouse,
  };
};
